import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../lib/api';

const FAST_POLL_MS = 10000;
const NORMAL_POLL_MS = 30000;
const BACKGROUND_POLL_MS = 60000;

export const useNotificationsRealtime = ({ enabled = true, fastMode = false } = {}) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const inFlightRef = useRef(false);

  const loadNotifications = useCallback(async ({ force = false } = {}) => {
    if (!enabled) {
      return;
    }
    if (inFlightRef.current && !force) {
      return;
    }

    inFlightRef.current = true;
    setLoading(true);
    try {
      const [notifRes, countRes] = await Promise.all([
        api.getNotifications(20),
        api.getNotificationCount(),
      ]);
      setNotifications(notifRes.data || []);
      setUnreadCount(countRes.data?.unread_count || 0);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  }, [enabled]);

  const pollingInterval = useMemo(() => {
    if (!enabled) {
      return 0;
    }
    if (typeof document !== 'undefined' && document.hidden) {
      return BACKGROUND_POLL_MS;
    }
    return fastMode ? FAST_POLL_MS : NORMAL_POLL_MS;
  }, [enabled, fastMode]);

  useEffect(() => {
    if (!enabled) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    loadNotifications({ force: true });
  }, [enabled, loadNotifications]);

  useEffect(() => {
    if (!enabled || !pollingInterval) {
      return;
    }

    const timer = setInterval(() => {
      loadNotifications();
    }, pollingInterval);

    return () => clearInterval(timer);
  }, [enabled, pollingInterval, loadNotifications]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const onFocus = () => loadNotifications();
    const onVisibilityChange = () => {
      if (!document.hidden) {
        loadNotifications();
      }
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enabled, loadNotifications]);

  return {
    notifications,
    unreadCount,
    loading,
    setNotifications,
    setUnreadCount,
    refreshNotifications: loadNotifications,
  };
};

export default useNotificationsRealtime;